import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { 
  BookOpen, ExternalLink, ArrowLeft, Code, Palette, 
  GitBranch, GraduationCap, MessageCircle, CheckCircle, Laptop
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  'book': BookOpen,
  'graduation-cap': GraduationCap,
  'palette': Palette,
  'code': Code,
  'git-branch': GitBranch,
  'laptop': Laptop,
  'message-circle': MessageCircle,
  'check-circle': CheckCircle
};

const StudentResources = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/student/login');
    } else if (user) {
      fetchResources();
    }
  }, [user, authLoading, navigate]);

  const fetchResources = async () => {
    try {
      const res = await axios.get(`${API}/resources`, { withCredentials: true });
      setResources(res.data);
    } catch (error) {
      console.error('Failed to load resources');
    } finally {
      setLoading(false);
    }
  };

  const categories = ['All', ...new Set(resources.map(r => r.category))];
  const filteredResources = filter === 'All' ? resources : resources.filter(r => r.category === filter);

  if (authLoading || loading) {
    return (
      <div className="pt-20 min-h-screen bg-[#060F22] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00AEEF]"></div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-[#060F22]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        <div className="flex items-center gap-4 mb-8">
          <Link to="/student/dashboard" className="text-[#94A3B8] hover:text-white">
            <ArrowLeft className="w-6 h-6" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-white">Resource Library</h1>
            <p className="text-[#94A3B8]">Helpful tools and learning materials</p>
          </div>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                filter === cat
                  ? 'bg-[#00AEEF] text-white'
                  : 'bg-[#0A1F44] text-[#94A3B8] hover:text-white border border-[#00AEEF]/20'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Resources Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {filteredResources.map((resource) => {
            const IconComponent = iconMap[resource.icon] || BookOpen;
            return (
              <a
                key={resource.id}
                href={resource.url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6 hover:border-[#00FFE0]/50 transition-all group"
              >
                <div className="w-12 h-12 rounded-xl bg-[#00AEEF]/20 flex items-center justify-center mb-4 group-hover:bg-[#00AEEF]/30 transition-colors">
                  <IconComponent className="w-6 h-6 text-[#00AEEF]" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                  {resource.title}
                  <ExternalLink className="w-4 h-4 text-[#94A3B8] group-hover:text-[#00FFE0]" />
                </h3>
                <p className="text-[#94A3B8] text-sm mb-3">{resource.description}</p>
                <span className="inline-block px-2 py-1 rounded-full text-xs bg-[#060F22] text-[#00AEEF]">
                  {resource.category}
                </span>
              </a>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StudentResources;
